<template>
    <v-form @submit.prevent="addTeamPlan" ref="form">
        <v-card class="rounded-4">
            <v-toolbar flat height="55px">
                <span class="font-weight-bold">Create Team Plan</span>
                <v-spacer></v-spacer>
                <v-btn icon @click.native="closeDialog()" >
                    <v-icon >close</v-icon>
                </v-btn>
            </v-toolbar>
            <v-card-text class="form-container mt-10">
                <v-layout row wrap>
                    <v-flex xs12 sm12 class="form-row">
                        <v-autocomplete
                            label="Team"
                            v-model="teamIds"
                            :disabled="disabled"
                            :items="teams"
                            :loading="loading_teams"
                            :rules="[rules.required]"
                            chips
                            color="primary lighten-2"
                            item-text="team"
                            item-value="teamId"
                            multiple
                            >
                            <template v-slot:selection="data">
                                <v-chip :selected="data.selected" class="chip--select-multi ml-0">
                                    {{ data.item.name }}
                                </v-chip>
                            </template>
                            <template v-slot:item="data">
                                <template>
                                    <v-list-tile-content>
                                        <v-list-tile-title v-html="data.item.name"></v-list-tile-title>
                                    </v-list-tile-content>
                                </template>
                            </template>
                        </v-autocomplete>
                    </v-flex>
                </v-layout>
                <v-layout row wrap>
                    <v-flex xs12 sm12 class="form-row"> 
                        <v-autocomplete
                            label="Workshift"
                            v-model="workShiftId"
                            :disabled="disabled"
                            :items="workshifts_data"
                            :loading="loading"
                            :rules="[rules.required]"
                            chips
                            color="primary"
                            item-text="name"
                            item-value="workShiftId"
                            @change="changeColor" 
                            >
                            <template v-slot:selection="data">
                                <v-chip :selected="data.selected" class="chip--select-multi ml-0" :color="color">
                                     <span :style="{'color': getContrastColor(color)}">
                                        {{ data.item.name }}
                                    </span>
                                </v-chip>
                            </template>
                            <template v-slot:item="data">
                                <template v-if="typeof data.item !== 'object'">
                                    <v-list-tile-content v-text="data.item"></v-list-tile-content>
                                </template>
                                <template v-else>
                                    <v-list-tile-content>
                                        <v-list-tile-title v-html="data.item.name"></v-list-tile-title>
                                        <v-list-tile-sub-title v-html="data.item.group"></v-list-tile-sub-title>
                                    </v-list-tile-content>
                                </template>
                            </template>
                        </v-autocomplete>
                    </v-flex>
                </v-layout> 
                <v-layout row wrap class="form-row mb-0">
                    <v-flex xs12 sm6 :class="isMobile?'':'pr-15'"> 
                        <v-menu v-model="menuStartDate" :close-on-content-click="false" :nudge-right="40"
                            lazy transition="scale-transition" offset-y full-width min-width="290px">
                            <template v-slot:activator="{ on }">
                                <v-text-field label="Start Date" v-model="startDate" readonly v-on="on" :rules="[rules.required]" :disabled="disabled"></v-text-field>
                            </template>
                            <v-date-picker no-title v-model="startDate" @input="updateData" :allowed-dates="allowedStartDates"></v-date-picker>
                        </v-menu>
                    </v-flex>   
                    <v-flex xs12 sm6 :class="isMobile?'':'pl-15'"> 
                        <v-menu v-model="menuEndDate" :close-on-content-click="false" :nudge-right="40"
                            lazy transition="scale-transition" offset-y full-width min-width="290px">
                            <template v-slot:activator="{ on }">
                                <v-text-field label="End Date" v-model="endDate" readonly v-on="on" :rules="[rules.required]" :disabled="disabled"></v-text-field>
                            </template>
                            <v-date-picker no-title v-model="endDate" @input="menuEndDate=false" :allowed-dates="allowedEndDates"></v-date-picker>
                        </v-menu>
                    </v-flex>   
                </v-layout>
                <v-layout row wrap class="form-row mt-n10 mb-15">
                    <v-flex xs12 sm12>
                        <v-switch
                            v-model="overwriteExisting"
                            label="Overwrite existing"
                            color="primary"
                            persistent-hint
                            :hint="overwriteExisting?'Will overwrite if workshift already exist':'Will not overwrite if workshift already exist'"
                        ></v-switch> 
                        <v-switch
                            v-model="includeWeekends"
                            label="Include weekends"
                            color="primary"
                            persistent-hint
                            :hint="includeWeekends?'Will include weekends if within selected date range':'Will not include weekends if within selected date range'"
                        ></v-switch> 
                    </v-flex>
                </v-layout>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn @click="clearForm" :disabled="disabled">Clear</v-btn>
                <v-btn color="primary" type="submit" :disabled="disabled" :loading="loading" dark style="margin-right:25px;">Save</v-btn>
            </v-card-actions>
        </v-card>
    </v-form>
</template>
<script>

import teamsApi from '@/api/team'
import workshiftsplanApi from '@/api/workshiftsplan'
import { getContrastColor } from '@/helpers/font-color'

export default {
    data () {
        return {
            loading: false,
            disabled: false,
            error: false,
            errorMessage: null,
            loading_teams: false,
            rules: {
                required: value => !!value || 'Required',
            },       
            teams: [],
            workshifts_data: [],
            teamIds: null,
            includeWeekends: false,
            overwriteExisting: false,
            menuStartDate: false,
            startDate: null,
            menuEndDate: false,
            endDate: null,
            workShiftId: null,
            selectedTeamId: null,
            fiscalYear: null,
            pageKey: null,
            color: null,
        }
    },
    methods: {
        getContrastColor,
        changeColor(val) {
            const data = this.workshifts_data.filter(x => x.workShiftId == val)
            if (data.length) {
                this.color = data[0].color
            } else {
                this.color = 'transparent'
            }
        },
        async getTeams() {
            try {
                this.loading_teams = true
                this.employees = []
                const response = await teamsApi.getTeamsByCompany(false)
                if (response && response.status == 200) {
                    if (this.selectedTeamId == 0) {
                        this.teams = response.data.items
                    }else{
                        this.teams = response.data.items.filter(x => x.teamId == this.selectedTeamId)
                        this.setDefaultTeam()
                    }
                }
                this.loading_teams = false
            }catch(error) {
                this.errorMessage = error
                this.error = true
            }
        },
        setDefaultTeam() {
            this.teamIds =  this.teams.map(x => x.teamId)
        },
        async getWorkShifts() {
            try {
                this.loading = true
                const response = await workshiftsplanApi.getWorkShifts(this.pageKey) 
                if (response && response.status == 200) {
                    this.workshifts_data = response.data.items
                }
                this.loading = false
            }catch(error) {
                this.error = true
                this.errorMessage = error
            }
        },
        async addTeamPlan() {
            try {
                this.loading = true
                if (this.$refs.form.validate()) {
                    this.loading = true
                    this.disabled = true
                    const data = {
                        teamId: this.selectedTeamId,
                        workShiftId: this.workShiftId,
                        startDate: this.startDate,
                        endDate: this.endDate,
                        userId: 0,
                        fiscalYear: this.fiscalYear,
                        includeWeekends: this.includeWeekends,
                        overwriteExisting: this.overwriteExisting,
                        teamIds: this.teamIds
                    }
                    const response = await workshiftsplanApi.updateByTeam(data, this.pageKey)
                    if (response && response.status == 200) {
                        this.$emit('send',null)                        
                        this.closeDialog()
                    }
                    this.disabled = false
                    this.loading = false
                }
                this.loading = false
            }catch(error){
                console.log(error)
                this.error = true
                this.errorMessage = error
            }
        },
        updateData() {
            this.menuStartDate=false
            if (this.$moment(this.startDate).format('YYYY-MM-DD') > this.$moment(this.endDate).format('YYYY-MM-DD')) {
                this.endDate = this.startDate
            }
        },
        allowedStartDates(val) {
            //allow only current or future dates in start date
            const currentDate = new Date()
            return this.$moment(val).format('YYYY-MM-DD') >= this.$moment(currentDate).format('YYYY-MM-DD')
        },
        allowedEndDates(val) {
            //allow only dates equal or greater than start date
            return this.$moment(val).format('YYYY-MM-DD') >= this.$moment(this.startDate).format('YYYY-MM-DD')
        },
        async setData(data) {
            this.selectedTeamId = data.teamId
            this.fiscalYear = data.fiscalYear
            this.pageKey = data.pageKey

            if (this.workshifts_data.length == 0) {
                await this.getWorkShifts()                
            }

            await this.getTeams() 
            this.setDefaultTeam()
        },
        closeDialog() {
            this.clearForm()
            this.$emit('update:dialogAddTeamPlan', false)
        },
        clearForm() {
            this.$refs.form.reset()
            this.loading = false
            this.error = false
            this.errorMessage = ''
            this.color = 'transparent'
        },
    },
}
</script>
<style scoped>
.form-container {
    padding: 0px 20px 0px 20px;
}
.form-row {
    padding: 0px 25px 0px 25px;
}
</style>